import { supabase } from "../supabase";
import {
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  buildSalePaymentTerms,
  deactivateSaleReceivable,
  hasClientCreditTerms,
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  setSalePaymentTermsColumnsSupport,
  shouldDeferNonConsumerCollection,
  shouldUseClientAccountForSale,
  syncSaleClientDebit,
  syncSaleReceivable,
} from "./salesReceivables";
import {
  registerImmediateStoreOrderFunds,
  revertImmediateStoreOrderFunds,
} from "./storeOrderFunds";
import { esVentaDeCanalExterno, etiquetaCanal } from "./ventaCanal";
import {
  getStoreOrderOrigin,
  isStoreOrderCanceledStatus,
  isStoreOrderDeliveredStatus,
  normalizeStoreOrderStatus,
} from "./storeLogistics";

function normalizeStoreOrderOrigin(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (["pos", "punto_venta", "punto-de-venta", "punto de venta", "auto_pos"].includes(normalized)) {
    return "pos";
  }
  if (["web", "tienda", "tienda_online", "tienda-online", "online"].includes(normalized)) {
    return "web";
  }
  return normalized;
}

function buildOrderReference(orderId, trackingCode) {
  return trackingCode || String(orderId || "").slice(0, 8);
}

function normalizePaymentMethodLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");
}

function paymentMethodUsesClientAccount(paymentMethod) {
  const normalized = normalizePaymentMethodLabel(paymentMethod);
  if (!normalized) return false;

  return (
    normalized.includes("cuenta corriente") ||
    normalized.includes("cta cte") ||
    normalized.includes("fiado") ||
    normalized.includes("credito cliente") ||
    normalized.includes("credito comercial")
  );
}

function shouldUseClientAccountForStoreOrder(organization, client, paymentMethod = "") {
  if (paymentMethodUsesClientAccount(paymentMethod)) return true;
  if (!client?.id) return false;
  return shouldDeferNonConsumerCollection(organization, client) || hasClientCreditTerms(client);
}

const STORE_ORDER_SALE_BASE_SELECT =
  "id, organization_id, client_id, site_id, total, created_at, payment_method, billing_source, channel";
const STORE_ORDER_SALE_SELECT = `${STORE_ORDER_SALE_BASE_SELECT}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;

async function fetchSaleForStoreOrderClientAccount(saleId) {
  const support = getSalePaymentTermsColumnsSupport();

  if (support !== false) {
    const response = await supabase
      .from("sales")
      .select(STORE_ORDER_SALE_SELECT)
      .eq("id", saleId)
      .maybeSingle();

    if (!response.error) {
      setSalePaymentTermsColumnsSupport(true);
      return {
        data: normalizeSalePaymentTermsRow(response.data),
        error: null,
      };
    }

    if (!isMissingSalePaymentTermsColumnsError(response.error)) {
      return response;
    }

    setSalePaymentTermsColumnsSupport(false);
  }

  const fallbackResponse = await supabase
    .from("sales")
    .select(STORE_ORDER_SALE_BASE_SELECT)
    .eq("id", saleId)
    .maybeSingle();

  if (fallbackResponse.error) {
    return fallbackResponse;
  }

  return {
    data: normalizeSalePaymentTermsRow(fallbackResponse.data),
    error: null,
  };
}

export async function syncStoreOrderClientAccount({
  orderId,
  saleId,
  organizationId,
  trackingCode,
  nextStatus,
  shippingCost = 0,
  orderOrigin = "",
}) {
  if (!orderId || !saleId || !organizationId) {
    return { skipped: true, reason: "missing_context" };
  }

  const normalizedStatus = normalizeStoreOrderStatus(nextStatus);
  const saleReference = buildOrderReference(orderId, trackingCode);

  const { data: sale, error: saleError } =
    await fetchSaleForStoreOrderClientAccount(saleId);

  if (saleError) throw saleError;
  if (!sale?.id) {
    return { skipped: true, reason: "sale_not_found" };
  }

  if (esVentaDeCanalExterno(sale)) {
    // S1 (2026-10-05, hallazgo del verificador): la venta ya se cobro en el
    // canal externo y no debe generar fondos ni cuenta corriente. Pero el
    // trigger de Postgres fn_sales_to_client_movement inserta igual un
    // client_movements 'VentaPOS' y suma el saldo del cliente en TODA venta
    // completed con client_id, sin mirar channel ni afecta_caja. Hay que
    // neutralizar esa deuda con la misma normalizacion que usa la cobranza
    // inmediata (shouldApply: false calcula el diff contra lo que ya inserto
    // el trigger y lo revierte), sin tocar fondos de caja/banco: por eso este
    // bloque no llama a registerImmediateStoreOrderFunds ni a
    // revertImmediateStoreOrderFunds, a diferencia del camino "immediate" de
    // mas abajo.
    if (sale.client_id) {
      await syncSaleClientDebit({
        saleId: sale.id,
        organizationId,
        clientId: sale.client_id,
        totalAmount: sale.total,
        movementType: "Venta Online",
        observations: `Venta por ${etiquetaCanal(sale) || "canal externo"}, ya cobrada: no genera cuenta corriente. Pedido #${saleReference}`,
        shouldApply: false,
      });
      await deactivateSaleReceivable(sale.id);
    }

    return { skipped: true, reason: "canal_externo" };
  }

  const normalizedOrigin = normalizeStoreOrderOrigin(orderOrigin || sale.billing_source || "");
  if (normalizedOrigin === "pos") {
    return { skipped: true, reason: "pos_order_managed_externally" };
  }

  const [organizationRes, clientRes] = await Promise.all([
    supabase
      .from("organizations")
      .select("id, defer_non_cf_sales_collection")
      .eq("id", organizationId)
      .maybeSingle(),
    sale.client_id
      ? supabase
          .from("clients")
          .select("id, full_name, tax_condition, payment_condition_days, default_payment_condition")
          .eq("id", sale.client_id)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);

  if (organizationRes.error) throw organizationRes.error;
  if (clientRes.error) throw clientRes.error;

  const organization = organizationRes.data;
  const client = clientRes.data;
  const salePaymentTerms = buildSalePaymentTerms({
    sale,
    client,
    createdAt: sale.created_at,
  });
  const shouldDefer = shouldUseClientAccountForStoreOrder(
    organization,
    client,
    sale.payment_method,
  ) || shouldUseClientAccountForSale({ organization, client, sale });
  const shouldApplyDeferredCollection = isStoreOrderDeliveredStatus(normalizedStatus);

  if (shouldDefer && !client?.id) {
    return { skipped: true, reason: "missing_client" };
  }

  if (shouldDefer && sale.client_id) {
    const observations = shouldApplyDeferredCollection
      ? `Venta online entregada. Pedido #${saleReference}`
      : `Venta online pendiente de entrega. Pedido #${saleReference}`;
    const dueDate = salePaymentTerms.dueDate;

    const clientDebitResult = await syncSaleClientDebit({
      saleId: sale.id,
      organizationId,
      clientId: sale.client_id,
      totalAmount: sale.total,
      movementType: "Venta Online",
      observations,
      shouldApply: shouldApplyDeferredCollection,
    });

    const receivableResult = await syncSaleReceivable({
      saleId: sale.id,
      organizationId,
      clientId: sale.client_id,
      siteId: sale.site_id || null,
      source: "online",
      description: `Pedido web #${saleReference}`,
      dueDate,
      totalAmount: sale.total,
      active: shouldApplyDeferredCollection,
    });

    return {
      applied: true,
      collectionMode: "deferred",
      clientDebitResult,
      receivableResult,
    };
  }

  if (sale.client_id) {
    await syncSaleClientDebit({
      saleId: sale.id,
      organizationId,
      clientId: sale.client_id,
      totalAmount: sale.total,
      movementType: "Venta Online",
      observations: `Normalizacion de cobranza inmediata. Pedido #${saleReference}`,
      shouldApply: false,
    });
  }

  await deactivateSaleReceivable(sale.id);

  if (isStoreOrderCanceledStatus(normalizedStatus) || !isStoreOrderDeliveredStatus(normalizedStatus)) {
    const revertResult = await revertImmediateStoreOrderFunds({
      organizationId,
      saleId: sale.id,
      trackingCode,
    });

    return {
      applied: true,
      collectionMode: "immediate",
      revertResult,
    };
  }

  const fundsResult = await registerImmediateStoreOrderFunds({
    sale,
    shippingCost,
    trackingCode,
  });

  return {
    applied: true,
    collectionMode: "immediate",
    fundsResult,
  };
}

export async function reconcileStoreOrdersClientAccounts({
  orders,
  organizationId,
}) {
  const safeOrders = (Array.isArray(orders) ? orders : []).filter((order) => {
    return Boolean(order?.sale_id);
  });

  if (!organizationId || safeOrders.length === 0) {
    return { skipped: true, reason: "nothing_to_reconcile" };
  }

  const results = await Promise.allSettled(
    safeOrders.map((order) =>
      syncStoreOrderClientAccount({
        orderId: order.id,
        saleId: order.sale_id,
        organizationId,
        trackingCode: order.tracking_code,
        nextStatus: order.status,
        shippingCost: order.shipping_cost || 0,
        orderOrigin: getStoreOrderOrigin(order),
      }),
    ),
  );

  return {
    applied: true,
    reconciled: results.filter((result) => result.status === "fulfilled").length,
    failed: results.filter((result) => result.status === "rejected").length,
    results,
  };
}
